import * as React from "react";
import { map, size } from "lodash";
import styled from "styled-components";
import {
  FontWeight,
  Spacing,
  dominant,
  transparentize,
  useTheme,
} from "@glossy";
import { Color } from "@glossy/colors";
import { border, content } from "@glossy/theme";
import { Locale } from "@linkfluence/models/locale";
import { canManageProject } from "@linkfluence/models/role";
import { natural } from "@tootools/sort";
import Button from "@ui-kit/button";
import Icon from "@ui-kit/icon";
import Link from "@ui-kit/utils/link";
import Loading from "@radarly/components/navigation/switcher/loading";
import Portrait from "@radarly/components/portrait/project";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { useCurrentUserTimezone } from "@radarly/core/user/use-cases";
import { useCurrentWorkspace } from "@radarly/core/workspace/use-cases";
import { tr } from "@radarly/i18n";
import { isRoot } from "@radarly/models/level";
import { Project } from "@radarly/models/project";
import { User } from "@radarly/models/user";
import { Workspace } from "@radarly/models/workspace";
import { IRouterService } from "@radarly/shared/router";
import { Page } from "@radarly/shared/router/pages";
import Pill from "./pill";

const WorkspaceLink = styled(Link)<{ $active: boolean }>`
  background-color: ${(props) =>
    props.$active ? transparentize(0.85, content(props)) : null};
  font-weight: ${(props) => (props.$active ? "bold" : null)};
  color: ${content};
  display: flex;
  align-items: center;
  width: 100%;
  font-size: 14px;
  height: 46px;
  padding: 0 16px;
  overflow: hidden;
  column-gap: 16px;

  &:hover {
    background-color: ${(props) => transparentize(0.85, content(props))};
    cursor: pointer;
    text-decoration: none;
  }
`;

const IPLink = styled(WorkspaceLink)`
  border-bottom: 1px solid ${border};
`;

const Label = styled.div`
  display: inline-block;
  overflow: hidden;
  vertical-align: middle;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: calc(100% - 70px);
`;

const Arrow = styled(Icon)`
  height: 46px;
`;

const Help = styled.div`
  color: ${Color.Ruby};
  text-align: center;
  cursor: pointer;
  text-decoration: underline;
`;

const Blocked = styled.div`
  padding: 0 0 75px;
`;

const CenterDrawing = styled(Icon)`
  position: relative;
  left: 50%;
  margin: 25px 0 25px -52px;
`;

const SocialWallButton = styled.div`
  border-top: solid 1px ${border};
  text-align: center;
  padding: 8px 0;

  a:hover {
    text-decoration: none;
  }
`;

const SettingsButton = styled.div`
  text-align: center;
  margin: 8px 0;
`;

const ProjectIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => transparentize(0.85, dominant(props))};
  width: 32px;
  height: 32px;
  border-radius: 4px;
`;

const DashboardTitle = styled.div`
  line-height: 46px;
  color: ${(props) => content(props, "secondary")};
  padding: 0 ${Spacing.MediumLarge};
  font-weight: ${FontWeight.Bold};
`;

const Header = styled.div`
  border-bottom: solid 1px ${(props) => content(props, "tertiary")};
  height: 42px;
  font-weight: bold;
  font-size: 14px;
  line-height: 42px;
`;

const HeaderLabel = styled.div`
  padding: 2px 16px;
`;

interface IProps {
  project: Project;
  workspaces: Workspace[];
  workspaceId: number;
  locale: Locale;
  user: User;
  clickBlockedProjectHelp?: () => void;
  loading?: boolean;
  onClickWorkspace?: (workspace: number) => void;
  router: IRouterService;
  withSingleProjectHeader?: boolean;
}

const NavigationSwitcherWorkspaces = ({
  workspaceId,
  workspaces,
  project,
  locale,
  clickBlockedProjectHelp,
  loading,
  user,
  onClickWorkspace,
  router,
  withSingleProjectHeader,
}: IProps) => {
  const theme = useTheme();
  const currentProject = useCurrentProject();
  const currentWorkSpace = useCurrentWorkspace();
  const timezone = useCurrentUserTimezone();
  //TODO : quick fix to not check the ip section in discovery, we need to have a more global fix if it is needed elsewhere
  const checkIPTab = project.id === currentProject.id && !currentWorkSpace;
  const projectIPHHomeLink = (
    <IPLink
      url={router.getProjectUrlFromPage(project, "project_home")}
      $active={checkIPTab}
      onClick={() => onClickWorkspace(null)}
    >
      <ProjectIcon>
        <Icon glyph="pages" size={16} color={theme.dominant} />
      </ProjectIcon>
      <Label>{tr(locale, "HEADER.CUSTOMPAGES.TITLE")}</Label>
      {checkIPTab && <Arrow glyph="check" />}
    </IPLink>
  );

  return (
    <>
      {loading && project.flags.isAccessible ? (
        <Loading />
      ) : (
        <>
          {project.flags.isAccessible ? (
            project.flags.isActive ? null : (
              <Pill
                label={tr(locale, "PROJECTS_OVERVIEW::PROJECT_PAUSED")}
                color={Color.DarkTangerine}
              />
            )
          ) : (
            <Blocked>
              <Pill
                label={tr(locale, "PROJECTS_OVERVIEW::PROJECT_BLOCKED")}
                color={Color.Ruby}
              />
              <CenterDrawing glyph="hourglass-arrow-cross" size={104} />
              <Help onClick={clickBlockedProjectHelp}>
                {tr(locale, "PROJECTS_OVERVIEW::LEARN_MORE")}
              </Help>
            </Blocked>
          )}
          {project.flags.isAccessible && (
            <>
              <>
                {projectIPHHomeLink}
                {withSingleProjectHeader && (
                  <Header>
                    <HeaderLabel>
                      {currentProject.flags.isAccessible &&
                      !loading &&
                      size(workspaces)
                        ? tr(locale, "SWITCHER::PROJECTS_NAME_DASHBOARDS", {
                            name: currentProject.label,
                            count: size(workspaces),
                          })
                        : tr(locale, "SWITCHER::PROJECTS_NAME", {
                            name: currentProject.label,
                          })}
                    </HeaderLabel>
                  </Header>
                )}
              </>
              <DashboardTitle>
                {tr(locale, "SWITCHER::HEADER_DASHBOARDS")}
              </DashboardTitle>
              {size(workspaces) ? (
                map(natural(workspaces, "label"), (workspace) => (
                  <WorkspaceLink
                    url={router.getWorkspaceUrlFromPage(
                      Page.workspaceHome,
                      project,
                      workspace.id,
                      { timezone }
                    )}
                    $active={!checkIPTab && workspaceId === workspace.id}
                    onClick={() => onClickWorkspace(workspace.id)}
                    key={workspace.id}
                  >
                    <Portrait
                      picture={workspace.imageUrl || project.imageUrl}
                      isAccessible={project.flags.isAccessible}
                      isActive={project.flags.isActive}
                    />
                    <Label>{workspace.label}</Label>
                  </WorkspaceLink>
                ))
              ) : (
                <div>
                  <Pill
                    label={tr(locale, "SWITCHER::NO_DASHBOARDS")}
                    color={Color.DimGrey}
                  />
                  <CenterDrawing glyph="ghost" size={104} />
                  {(canManageProject(project.role) || isRoot(user.level)) && (
                    <SettingsButton>
                      <a
                        href={router.getSettingsUrl(project)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button text={tr(locale, "SETTINGS.TITLE")} />
                      </a>
                    </SettingsButton>
                  )}
                </div>
              )}
            </>
          )}
          {project.flags.isAccessible && !!project.flags.isSocialWallActivated && (
            <SocialWallButton>
              <a
                href={router.getSocialWallUrl(project)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="secondary"
                  text={tr(locale, "SOCIAL_WALL.LAUNCH_SOCIAL_WALL")}
                />
              </a>
            </SocialWallButton>
          )}
        </>
      )}
    </>
  );
};

export default NavigationSwitcherWorkspaces;
